<template>
  <div class="cig-map" :style="style">
    <div class="map-box" :id="id"></div>
  </div>
</template>
<script lang="ts">
import { defineComponent, PropType, reactive, ref, watch } from 'vue'
import { initMap } from './map-service'
import { mapControl, mapSkill } from './mapApi'
import control from './control.js'
import {
  CircleOptions,
  MarkerOptions,
  PolygonOptions,
  PolylineOptions,
} from './type'
export default defineComponent({
  name: 'ElCigMap',
  props: {
    width: {
      type: String,
      required: true,
    },
    height: {
      type: String,
      required: true,
    },
    src: {
      type: String,
      default:
        'https://api.map.baidu.com/api?v=1.0&&type=webgl&ak=trmAhlzjterbRHweVEssDAidh0PX7sOG&callback=initialize',
    },
    apiName: {
      type: String,
      default: 'BMapGL',
    },
    defaultOption: {
      type: Object as PropType<{ lat: number; lng: number }>,
      default: {
        lat: 0,
        lng: 0,
      },
    },
    is3D: {
      type: Boolean,
      default: false,
    },
    zoom: {
      type: Boolean,
      default: false,
    },
    scale: {
      type: Boolean,
      default: false,
    },
    scrollZoom: {
      type: Boolean,
      default: false,
    },
    dragging: {
      type: Boolean,
      default: true,
    },
    doubleClickZoom: {
      type: Boolean,
      default: true,
    },
    zooms: {
      type: Array as PropType<Array<Number>>,
      default: () => [],
    },
    clear: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, ctx) {
    // init here
    var map: any = null
    const id = 'map' + new Date().getTime().toString()
    const style = reactive({
      width: props.width,
      height: props.height,
    })
    const mapRef = ref()
    const mapApi = ref()
    initMap(props.src, props.apiName).then((res) => {
      map = res
      setTimeout(() => {
        mapApi.value = new map.Map(id)
        const {
          navi3DCtrl,
          switch3DCtrl,
          zoomCtrl,
          switchZoomCtrl,
          setMinZoom,
          setMaxZoom,
          scaleCtrl,
          switchScaleCtrl,
          clearCtrl,
          switchClearCtrl,
        } = mapControl(mapApi, map)
        // skill
        const { switchScrollZoom, switchDragging, switchDoubleClickZoom } =
          mapSkill(mapApi)
        // 自定义控件类

        navi3DCtrl.value = new map.NavigationControl3D() // 添加3D控件
        zoomCtrl.value = new map.ZoomControl() // 添加缩放控件
        scaleCtrl.value = new map.ScaleControl() // 添加比例尺控件

        control.customCtrl.prototype = new map.Control()
        control.customCtrl.prototype.initialize = function () {
          var dom = document.createElement('div')
          dom.appendChild(document.createTextNode(this.buttonText))
          // 设置样式
          dom.style.cursor = 'pointer'
          dom.style.padding = '7px 10px'
          dom.style.boxShadow = '0 2px 6px 0 rgba(27, 142, 236, 0.5)'
          dom.style.borderRadius = '5px'
          dom.style.backgroundColor = 'white'
          if (this.style) {
            for (let i in this.style) {
              dom.style[i as any] = this.style[i]
            }
          }

          if (this.listener) {
            for (let i in this.listener) {
              dom.addEventListener(i, () => {
                this.listener[i]()
              })
            }
          }
          mapApi.value.getContainer().appendChild(dom)
          return dom
        }
        clearCtrl.value = customCtrl(
          {
            buttonText: '清空覆盖物',
            offset: [20, 20],
          },
          {},
          {
            click: () => {
              clearOverlay()
            },
          }
        )
        watch(
          () => props.is3D,
          (newV) => {
            switch3DCtrl(newV)
          },
          {
            immediate: true,
          }
        )
        watch(
          () => props.zoom,
          (newV) => {
            switchZoomCtrl(newV)
          },
          {
            immediate: true,
          }
        )
        watch(
          () => props.scale,
          (newV) => {
            switchScaleCtrl(newV)
          },
          {
            immediate: true,
          }
        )
        watch(
          () => props.scrollZoom,
          (newV) => {
            switchScrollZoom(newV)
          },
          {
            immediate: true,
          }
        )
        watch(
          () => props.dragging,
          (newV) => {
            switchDragging(newV)
          },
          {
            immediate: true,
          }
        )
        watch(
          () => props.doubleClickZoom,
          (newV) => {
            switchDoubleClickZoom(newV)
          },
          {
            immediate: true,
          }
        )
        watch(
          () => props.zooms,
          (newV) => {
            if (newV.length > 1) {
              setMinZoom(newV[0])
              setMaxZoom(newV[1])
            }
          },
          {
            immediate: true,
          }
        )
        watch(
          () => props.clear,
          (newV) => {
            switchClearCtrl(newV)
          },
          {
            immediate: true,
          }
        )

        ctx.emit('onLoad', map, mapApi)
      }, 1500)
    })
    let markerList: any[] = []
    function addPoint(lng: any, lat: any) {
      return new map.Point(lng, lat)
    }
    function addMarker(point: any, options?: MarkerOptions, listener?: any) {
      const pointMap = addPoint(point.lng, point.lat)
      const marker = new map.Marker(pointMap, options)
      const row: any = { row: point, pointMap, marker }
      const oldRow = markerList.findIndex((x: any) =>
        x.pointMap.equals(pointMap)
      )
      if (oldRow > -1) {
        throw new Error('当前点位已存在！')
      } else {
        mapApi.value.addOverlay(marker)
        markerList.push(row)
      }
      if (listener) {
        for (let i in listener) {
          marker.addEventListener(i, () => {
            // const index = markerList.findIndex(x => x.lat !== point.lat && x.lng !== point.lng)
            listener[i](row)
          })
        }
      }
      return marker
    }
    function addIcon(url: string, size: Array<number>) {
      return new map.Icon(url, new map.Size(size[0], size[1]))
    }

    function removeMarker(point: any) {
      const oldRow = markerList.findIndex((x) =>
        x.pointMap.equals(addPoint(point.lng, point.lat))
      )
      if (oldRow === -1) {
        throw new Error('当前点位不存在，无法删除！')
      } else {
        mapApi.value.removeOverlay(markerList[oldRow].marker)
        markerList.splice(oldRow, 1)
      }
    }
    function addLine(
      line: { [key: string]: any; points: Array<any> },
      options?: PolylineOptions,
      listener?: any
    ) {
      const polyline = new map.Polyline(line.points, options)
      mapApi.value.addOverlay(polyline)
      if (listener) {
        for (let i in listener) {
          polyline.addEventListener(i, () => {
            // const index = markerList.findIndex(x => x.lat !== point.lat && x.lng !== point.lng)
            listener[i](line)
          })
        }
      }
      return polyline
    }
    function removeLine(line: any) {
      mapApi.value.removeOverlay(line)
    }

    function addPolygon(
      line: { [key: string]: any; points: Array<any> },
      options?: PolygonOptions,
      listener?: any
    ) {
      const polygon = new map.Polygon(line.points, options)
      mapApi.value.addOverlay(polygon)
      if (listener) {
        for (let i in listener) {
          polygon.addEventListener(i, () => {
            // const index = markerList.findIndex(x => x.lat !== point.lat && x.lng !== point.lng)
            listener[i](line)
          })
        }
      }
      return polygon
    }

    function removePolygon(polygon: any) {
      mapApi.value.removeOverlay(polygon)
    }

    function addCircle(point: any, options?: CircleOptions, listener?: any) {
      const circle = new map.Circle(
        addPoint(point.lat, point.lng),
        point.radius,
        options
      )
      mapApi.value.addOverlay(circle)
      if (listener) {
        for (let i in listener) {
          circle.addEventListener(i, () => {
            // const index = markerList.findIndex(x => x.lat !== point.lat && x.lng !== point.lng)
            listener[i](point)
          })
        }
      }
      return circle
    }

    function removeCircle(circle: any) {
      mapApi.value.removeOverlay(circle)
    }

    // 清除地图上所有/可选覆盖物
    function clearOverlay(markers: Array<any> = []) {
      if (markers.length > 0) {
        markers.forEach((x: any) => {
          mapApi.value.removeOverlay(x)
        })
      } else {
        markerList = []
        mapApi.value.clearOverlays()
      }
    }
    function customCtrl(options?: any, style?: any, listener?: any) {
      return new (control.customCtrl as any)(map, options, style, listener)
    }
    function createCustomCtrl(options?: any, style?: any, listener?: any) {
      const ctrl = customCtrl(options, style, listener)
      mapApi.value.addControl(ctrl)
      return ctrl
    }
    // 3D控件对象

    ctx.expose({
      mapRef: mapRef,
      mapApi,
      addMarker,
      addPoint,
      addIcon,
      removeMarker,
      clearOverlay,
      addLine,
      removeLine,
      addPolygon,
      removePolygon,
      addCircle,
      removeCircle,
      createCustomCtrl,
    })
    return {
      id,
      style,
    }
  },
})
</script>
